import ls from 'localstorage-slim';
import type { FolderApi } from 'tweakpane';
import { Pane } from 'tweakpane';
import type { Container } from 'pixi.js';
import type { LayoutListEntry } from '../../../libs/layouts/runtime/layoutModules';
import { LayoutGalleryDisplay } from './LayoutGalleryDisplay';
import type { LayoutGalleryDisplaySettings } from './LayoutGalleryDisplay';
import { preloadLayoutsAssets } from '../../../views/preloadLayoutsAssets';
import { playLayoutAnimation } from '../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { applyLayoutState } from '../../../libs/layouts/populateLayouts/applyLayoutState';

interface LayoutGalleryReloadState {
  displaySettings: LayoutGalleryDisplaySettings;
  currentLayout: string | null;
  currentAnimation: string | null;
  currentSkin: string | null;
  loopAnimation: boolean;
}

export class LayoutGallery {
  private readonly lsKey = 'Layout_Gallery_RELOAD_STATE';

  private display: LayoutGalleryDisplay | null = null;

  private currentLayout: string | null = null;

  private currentSkin: string | null = null;

  private currentAnimation: string | null = null;

  public loopAnimation = false;

  private readonly subFolders: Record<string, FolderApi> = {};

  constructor(private readonly layouts: LayoutListEntry[]) {}

  public checkForAutomaticOpening(): void {
    if (ls.get(this.lsKey) !== null) {
      this.open();
    }
  }

  public open(): void {
    const pane = this.createMainPane();

    const reloadState: LayoutGalleryReloadState | null = ls.get(this.lsKey);

    this.display = new LayoutGalleryDisplay(reloadState?.displaySettings ?? {});
    this.addPlaybackMenu(pane.tools);
    this.display.addMenu(pane.tools);
    this.createLayoutMenu(pane.layouts);

    if (reloadState?.currentLayout) {
      this.loopAnimation = reloadState.loopAnimation;
      void this.instantiateLayout(reloadState.currentLayout, null, reloadState.currentAnimation);
    }
  }

  private createLayoutFolder(rootFolder: FolderApi, path: string, title: string): FolderApi {
    let current = '';
    let currentFolder: FolderApi = rootFolder;

    const chunks = path.split('/');

    for (let i = 0; i < chunks.length; i += 1) {
      const chunk = chunks[i];
      if (chunk === '') continue;
      if (chunk === 'layouts') continue;

      if (i === chunks.length - 1) {
        return currentFolder.addFolder({ title, expanded: true });
      }

      current += `/${chunk}`;
      if (!this.subFolders[current]) {
        this.subFolders[current] = currentFolder.addFolder({ title: chunk, expanded: false });
      }
      currentFolder = this.subFolders[current];
    }

    throw new Error('Unable to create layout folder');
  }

  private createLayoutMenu(pane: Pane): void {
    const rootFolder = pane.addFolder({ title: 'Layouts' });

    this.layouts.forEach((entry) => {
      const { raw } = entry.ctr;
      const stateNames = (raw as { stateNames?: string[] })?.stateNames ?? [];
      const animations = (raw as { animations?: { name: string }[] }).animations ?? [];

      const layoutFolder = this.createLayoutFolder(rootFolder, entry.path, entry.name);

      layoutFolder.addButton({ title: 'Skin: default' }).on('click', () => {
        void this.instantiateLayout(entry.path, null, null);
      });

      stateNames.forEach((skin) => {
        layoutFolder.addButton({ title: `Skin: ${skin}` }).on('click', () => {
          void this.instantiateLayout(entry.path, skin, null);
        });
      });

      animations.forEach(({ name }) => {
        layoutFolder.addButton({ title: `Animation: ${name}` }).on('click', () => {
          void this.instantiateLayout(entry.path, this.currentSkin ?? null, name);
        });
      });
    });
  }

  private async instantiateLayout(layoutPath: string, skin: string | null, animation: string | null): Promise<void> {
    const layoutEntry = this.layouts.find((entry) => entry.path === layoutPath);
    if (!layoutEntry) return;
    if (!this.display) return;

    this.currentLayout = layoutPath;
    this.currentAnimation = animation;
    this.currentSkin = skin;

    const layout = new (layoutEntry.ctr as unknown as new () => Record<string, unknown>)();
    if (skin) applyLayoutState(layout, skin);
    await preloadLayoutsAssets([layoutEntry.ctr]);
    this.display.setNewLayout(layout.root as Container);
    if (animation) {
      void playLayoutAnimation(layout, animation, this.loopAnimation);
    }
  }

  private addPlaybackMenu(pane: Pane): void {
    const folder = pane.addFolder({ title: 'Playback' });

    folder.addButton({ title: 'Lock current layout on reload' }).on('click', () => {
      if (!this.display) return;

      ls.set<LayoutGalleryReloadState>(this.lsKey, {
        displaySettings: this.display.getDisplaySettings(),
        currentLayout: this.currentLayout,
        currentSkin: this.currentSkin,
        currentAnimation: this.currentAnimation,
        loopAnimation: this.loopAnimation,
      });
    });

    folder.addButton({ title: 'Clear reload' }).on('click', () => {
      ls.set(this.lsKey, null);
    });

    folder.addButton({ title: 'Clear' }).on('click', () => {
      this.display?.setNewLayout(null);
    });
    folder.addBinding(this, 'loopAnimation');
  }

  private createMainPane(): { tools: Pane; layouts: Pane } {
    const pane = new Pane();
    const paneDiv = document.createElement('div');
    Object.assign(paneDiv.style, {
      position: 'fixed',
      top: 0,
      right: '360px',
      bottom: 0,
    });
    paneDiv.appendChild(pane.element);
    document.body.appendChild(paneDiv);

    const layoutPane = new Pane();
    const layoutPaneDiv = document.createElement('div');
    Object.assign(layoutPaneDiv.style, {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      overflowY: 'scroll',
    });
    layoutPaneDiv.appendChild(layoutPane.element);
    document.body.appendChild(layoutPaneDiv);

    return { tools: pane, layouts: layoutPane };
  }
}
